import React, { Component } from 'react'
import { connect } from 'react-redux'
import history from 'browserHistory'
import { compose } from 'recompose'

import { Button, Text } from 'components/base'
import Breadcrumb from './Breadcrumb'
import PlanSelector from '../components/PlanSelector'
import Separator from './Separator'
import ErrorMessage from '../components/ErrorMessage'
import CompanyDetailsSection, {
  checker,
  getEmptyCompanyDetailsValues,
} from '../components/CompanyDetailsSection'
import CardSection from '../components/CardSection'
import Review from './Review'

import { trackStepInUpgrade } from 'data/analytics/actions'
import providesOrganizationPaymentInfos from '../../dataSync/providesOrganizationPaymentInfos'
import providesAvailablePlans from '../dataSync/providesAvailablePlans'
import providesSubscribeToPlan from '../dataSync/providesSubscribeToPlan'
import { selectors as UsersSelectors } from 'data/users'
import { selectors as OrganizationsSelectors } from 'data/organizations'
import { getSortedlistOfAvailablePlans } from 'data/payment/plans'
import { selectors as PaymentSelectors } from 'data/payment'
import RoutesHelper from 'helpers/RoutesHelper'

const STEPS = {
  INIT: 0,
  COMPANY_DETAILS: 1,
  PAYMENT_METHOD: 2,
  REVIEW: 3,
}

const SCROLL_OPTIONS = { behavior: 'smooth', block: 'start' }

class SettingsBillingUpgrade extends Component {
  state = {
    step: STEPS.INIT,
    selectedPlanIndex: 1,

    companyDetails: getEmptyCompanyDetailsValues(),
    companyDetailsErrors: null,

    card: {},

    tokenExchangeOngoing: false,
    token: {},
    tokenError: null,

    cardholderName: '',

    subscriptionOngoing: false,
    subscriptionError: null,
  }
  componentDidMount() {
    this.props.trackStepInUpgrade({
      step: 'PLAN SELECTION',
      planSelected: 'none',
    })
  }
  render() {
    if (!this.props.hasAccess) {
      return <div>You don't have access to this section</div>
    }

    if (this.props.paymentInfoFetchState.error) {
      return (
        <ErrorMessage
          retry={this.props.forceQuery}
          action="getting your billing information"
          message={this.props.paymentInfoFetchState.error.message}
        />
      )
    }

    if (
      this.props.paymentInfoFetchState.loading ||
      this.props.paymentInfoFetchState.pristine
    ) {
      return <div>Loading your billing information...</div>
    }

    if (
      !this.props.plans.length ||
      !this.props.organization ||
      !this.props.organization.activeUsersCount
    ) {
      return null
    }

    if (this.state.step === STEPS.REVIEW) {
      return this.renderReview()
    }

    return (
      <div className="slite-design-system">
        <Text.Subtitle className="margin-vertical-larger">
          Upgrade your team's plan
        </Text.Subtitle>
        <PlanSelector
          plans={this.props.plans}
          selectedPlanIndex={this.state.selectedPlanIndex}
          onSelectPlan={selectedPlanIndex =>
            this.setState({ selectedPlanIndex })
          }
          onSelectAndUpgrade={this.onSelectAndUpgrade}
          activeUsersCount={this.props.organization.activeUsersCount}
          coupon={this.props.coupon}
        />
        {this.renderCompanyDetailsSection()}
        {this.renderPaymentMethod()}
        {this.renderGoToReviewButton()}
      </div>
    )
  }

  renderBreadcrumb(step, ref) {
    const currentSectionIndex = [
      STEPS.COMPANY_DETAILS,
      STEPS.PAYMENT_METHOD,
    ].indexOf(step)

    return (
      <Breadcrumb
        sections={['Company details', 'Payment method']}
        currentSectionIndex={currentSectionIndex}
        innerRef={ref}
      />
    )
  }

  renderCompanyDetailsSection() {
    if (this.state.step < STEPS.COMPANY_DETAILS) {
      return null
    }

    return (
      <React.Fragment>
        <Separator />
        {this.renderBreadcrumb(
          STEPS.COMPANY_DETAILS,
          ref => (this.companyDetailsSection = ref)
        )}
        <div className="font-size-smaller shade-20 margin-vertical-large">
          This information will be included on all billing invoices on your
          account.
        </div>
        <CompanyDetailsSection
          values={this.state.companyDetails}
          errors={this.state.companyDetailsErrors}
          onChange={this.onCompanyDetailsChange}
        />
      </React.Fragment>
    )
  }

  renderPaymentMethod() {
    if (this.state.step < STEPS.PAYMENT_METHOD) {
      return null
    }

    return (
      <React.Fragment>
        <Separator />
        {this.renderBreadcrumb(
          STEPS.PAYMENT_METHOD,
          ref => (this.paymentMethodSection = ref)
        )}
        <div className="margin-top-normal font-size-smaller shade-20 margin-bottom-larger">
          Set your payment information
        </div>
        <CardSection
          cardholderName={this.state.cardholderName}
          onCardholderNameChange={this.onCardholderNameChange}
          onCardChange={this.onCardChange}
        />
      </React.Fragment>
    )
  }

  renderReview() {
    if (this.state.step < 2) {
      return null
    }

    return (
      <Review
        activeUsersCount={this.props.organization.activeUsersCount}
        companyDetails={this.state.companyDetails}
        token={this.state.token}
        coupon={this.props.coupon}
        plan={this.props.plans[this.state.selectedPlanIndex]}
        onConfirm={this.onConfirm}
        onGoToCompanyEditSection={this.onGoToCompanyEditSection}
        onGoToPaymentMethodSection={this.onGoToPaymentMethodSection}
        onSelectAlternativeInterval={this.onSelectAlternativeInterval}
        subscriptionOngoing={this.state.subscriptionOngoing}
        subscriptionError={this.state.subscriptionError}
        onBack={this.onGoToCompanyEditSection}
      />
    )
  }

  getErrorMessage() {
    if (this.state.companyDetailsErrors) {
      return 'Errors in the company details section'
    }
    if (this.state.card.error) {
      return this.state.card.error.message
    }
    if (this.state.tokenError) {
      return this.state.tokenError.message
    }
    return null
  }

  getMissingStepMessage() {
    if (this.state.selectedPlanIndex == null) {
      return 'Please select a plan'
    }
    if (!this.state.card.complete) {
      return 'Please fill your card details'
    }
    return null
  }

  renderGoToReviewButton() {
    if (this.state.step < STEPS.PAYMENT_METHOD) {
      return null
    }

    const errorMessage = this.getErrorMessage()

    const loadingMessage = this.state.tokenExchangeOngoing
      ? 'Checking your card details…'
      : null

    const missingStepMessage = this.getMissingStepMessage()

    const disabledMessage = errorMessage || loadingMessage || missingStepMessage

    const label = disabledMessage || 'Next: Review your info'

    return (
      <Button.Primary
        className="margin-vertical-larger"
        onClick={this.goToReview}
        disabled={disabledMessage}
      >
        {label}
      </Button.Primary>
    )
  }

  onSelectAndUpgrade = selectedPlanIndex => {
    // Go to next step unless already ahead
    const step =
      this.state.step === STEPS.INIT ? STEPS.COMPANY_DETAILS : this.state.step

    this.setState({ step, selectedPlanIndex }, () => {
      this.companyDetailsSection.scrollIntoView(SCROLL_OPTIONS)
    })
    this.props.trackStepInUpgrade({
      step: 'COMPANY DETAILS',
      planSelected: selectedPlanIndex,
    })
  }

  onCompanyDetailsChange = companyDetails => {
    const companyDetailsErrors = checker(companyDetails)

    // Enable next step as soon as the details are valid
    const step = companyDetailsErrors ? this.state.step : STEPS.PAYMENT_METHOD

    this.setState(
      {
        companyDetails,
        companyDetailsErrors,
        step,
      },
      () => {
        if (step === STEPS.PAYMENT_METHOD) {
          this.props.trackStepInUpgrade({
            step: 'PAYMENT METHOD',
            planSelected: this.state.selectedPlanIndex,
          })
          this.paymentMethodSection.scrollIntoView(SCROLL_OPTIONS)
        }
      }
    )
  }

  onCardholderNameChange = cardholderName => {
    this.setState({
      cardholderName,
    })
  }

  onCardChange = (stripe, card) => {
    this.setState({ card })

    if (card.complete) {
      this.setState({ tokenExchangeOngoing: true })
      stripe
        .createToken({
          name: this.state.cardholderName,
          address_line1: this.state.companyDetails.address,
          address_line2: this.state.companyDetails.address2,
          address_city: this.state.companyDetails.city,
          address_state: this.state.companyDetails.state,
          address_zip: this.state.companyDetails.zip,
          address_country: this.state.companyDetails.country,
        })
        .then(payload => {
          if (payload.error) {
            this.setState({
              tokenExchangeOngoing: false,
              tokenError: payload.error,
              token: null,
            })
          } else {
            this.setState({
              tokenExchangeOngoing: false,
              token: payload.token,
              tokenError: null,
            })
          }
        })
    }
  }

  onConfirm = () => {
    this.setState({ subscriptionOngoing: true })
    this.props.trackStepInUpgrade({
      step: 'PAYMENT ONGOING',
      planSelected: this.state.selectedPlanIndex,
    })
    this.props
      .subscribeToPlan({
        input: {
          planId: this.props.plans[this.state.selectedPlanIndex].id,
          token: this.state.token.id,
          email: this.state.companyDetails.email,
          vatId: this.state.companyDetails.vatId,
          trial: false,
          addressName: this.state.companyDetails.companyName,
          addressLine1: this.state.companyDetails.address,
          addressLine2: this.state.companyDetails.address2,
          addressCity: this.state.companyDetails.city,
          addressState: this.state.companyDetails.state,
          addressPostalCode: this.state.companyDetails.zip,
          addressCountry: this.state.companyDetails.country,
        },
      })
      .then(
        result => {
          this.setState({ subscriptionOngoing: false })
          history.push(
            RoutesHelper.settingsOrganization({
              tabId: 'billing',
              options: 'overview',
              messageId: 'subscriptionSuccess',
            })
          )
          this.props.trackStepInUpgrade({
            step: 'PAYMENT SUCCESSFUL',
            planSelected: this.state.selectedPlanIndex,
          })
        },
        ({ error }) => {
          this.setState({
            subscriptionOngoing: false,
            subscriptionError: error,
          })
        }
      )
  }

  goToReview = () => {
    this.setState({
      step: STEPS.REVIEW,
    })
    this.props.trackStepInUpgrade({
      step: 'REVIEW',
      planSelected: this.state.selectedPlanIndex,
    })
  }

  onSelectAlternativeInterval = () => {
    const currentPlan = this.props.plans[this.state.selectedPlanIndex]
    const altPlanIndex = this.props.plans.findIndex(plan => {
      return plan.id === currentPlan.altIntervalId
    })
    if (altPlanIndex == null) {
      throw new Error(
        `Unable to find alternative plan ${currentPlan.altIntervalId}`
      )
    }
    this.setState({
      selectedPlanIndex: altPlanIndex,
    })
  }

  onGoToCompanyEditSection = () => {
    this.setState(
      {
        step: STEPS.COMPANY_DETAILS,
      },
      () => this.companyDetailsSection.scrollIntoView(SCROLL_OPTIONS)
    )
  }

  onGoToPaymentMethodSection = () => {
    this.setState(
      {
        step: STEPS.PAYMENT_METHOD,
      },
      () => this.paymentMethodSection.scrollIntoView(SCROLL_OPTIONS)
    )
  }
}

export default compose(
  providesOrganizationPaymentInfos,
  providesAvailablePlans,
  providesSubscribeToPlan,
  connect(
    state => ({
      hasAccess: UsersSelectors.hasAccessToFreeBillingViews(state),
      plans: getSortedlistOfAvailablePlans(state),
      coupon: PaymentSelectors.getOrganizationCoupon(state),
      organization: OrganizationsSelectors.getOrganization(state),
    }),
    {
      trackStepInUpgrade,
    }
  )
)(SettingsBillingUpgrade)
